import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import type { HelpSlug } from '../help/types';

/**
 * Small hover/focus tooltip used next to a section header to explain scope or
 * behavior. Question-mark icon in muted ink; on hover (or keyboard focus) a
 * light accent-tinted bubble pops up with the help copy.
 *
 * The bubble renders through a portal to <body> with `position: fixed`,
 * positioned from the trigger's bounding rect. That's deliberate: an in-flow
 * absolutely-positioned bubble gets clipped by any ancestor with
 * `overflow: auto/hidden` (the inbox panes, scroll containers, cards) and no
 * z-index can rescue it. Fixed-on-body escapes every clipping ancestor.
 *
 * It opens upward by default and flips below when the trigger is near the top
 * of the viewport; horizontally it's centered on the icon and clamped to the
 * viewport edges so it's never cut off.
 *
 * Closing is delayed (CLOSE_DELAY_MS) and cancelled whenever the pointer
 * re-enters either the trigger or the bubble itself, so crossing the gap
 * between them — to reach the optional `learnMore` link — doesn't race the
 * timer shut. The delay is deliberately generous: the bubble can land
 * horizontally offset from the trigger (clamped to the viewport edge), so a
 * straight mouse path from icon to bubble isn't always a short hop.
 *
 * Click (or Enter/Space) on the trigger also PINS the bubble open — it stays
 * up regardless of hover/blur until: click/tap outside, Escape, clicking the
 * trigger again, or anything under it scrolling (a bubble placed from a rect
 * that has since moved points at the wrong thing). The pinned Escape is
 * stopped where it is handled, so dismissing a tip inside a modal does not
 * also close the modal. That's the deterministic path for touch (no hover at
 * all) and for keyboard users tabbing from the trigger into the bubble's
 * "Learn more" link (which otherwise races the same close timer). Hover
 * behavior is unchanged on top of it.
 *
 * Anchor it to a section eyebrow (Labor / Equipment / page title), not per row.
 * The one exception is the labeled variant (`label`), which is a trigger the
 * reader is meant to notice and click rather than a passive `?` — see the prop.
 */
const BUBBLE_WIDTH = 300;
/* The caret diamond protrudes ~7px past the bubble edge; the gap has to
   clear it or the point touches the trigger icon. */
const GAP = 9;
const EDGE = 8;
const CLOSE_DELAY_MS = 250;

export function HelpTip({
  children,
  title,
  learnMore,
  inverse = false,
  label,
}: {
  children: ReactNode;
  /** Optional bold lead line above the body copy — a short noun phrase naming
   *  what the tip is about, not a sentence. */
  title?: string;
  learnMore?: HelpSlug;
  /** White trigger for dark/colored fills (e.g. the red duplicate banner),
   *  where the default muted-ink glyph all but disappears. Inline style so it
   *  also overrides the :hover ink shift — constant white is the point. */
  inverse?: boolean;
  /**
   * Render a short TEXT trigger instead of the question-mark glyph
   * ("Full details" on the reopened-for-revision banner). The glyph means
   * "there is an explanation here if you want it"; a labeled trigger means
   * "the rest of this statement is behind me", which a banner stating a
   * headline needs and a `?` cannot say. Everything else — bubble, portal,
   * pin, caret, dismissal — is identical, which is the reason this is a
   * variant and not a second component.
   */
  label?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  // The bubble renders through a portal, so the trigger and its content are
  // nowhere near each other in the DOM. `aria-describedby` is id-based and
  // crosses that gap; without it a screen reader gets a control named
  // "Full details" and no way to reach what it revealed.
  const bubbleId = useId();
  const closeTimer = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    /** Trigger center-x expressed relative to the bubble's left edge, for the
     *  CSS caret — stays honest even when `left` above is clamped to the
     *  viewport and no longer centered on the trigger. */
    caretX: number;
    /** Direction the bubble opened, per show()'s openUp — drives which
     *  `help-tip-bubble--up|--down` modifier class (and caret side) renders. */
    openUp: boolean;
  }>({ left: 0, caretX: BUBBLE_WIDTH / 2, openUp: true });

  // Belt-and-suspenders: if the component unmounts (e.g. navigation) while a
  // close timer is still pending, don't let it fire against a gone component.
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  // While pinned, dismiss on outside click/tap or Escape — the only two ways
  // out besides re-clicking the trigger (handled by togglePin below). Only
  // wired up while pinned, so the common hover-only path pays nothing extra.
  useEffect(() => {
    if (!pinned) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || bubbleRef.current?.contains(target)) return;
      setPinned(false);
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Escape dismisses the TIP, not whatever is behind it. Modals listen for
      // Escape on `window`, which is the last thing a bubbling key event
      // reaches, so without this a pinned tip and its window both went away on
      // one press — and the reader who opened the tip loses the screen they
      // opened it from. Only reachable while pinned (the effect's own guard),
      // so an unpinned hover tip still lets Escape through to close the window.
      e.stopPropagation();
      setPinned(false);
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [pinned]);

  // A fixed bubble is placed ONCE, from the trigger's rect, so anything that
  // moves the trigger afterwards leaves the bubble behind — still pointing at
  // the spot, now labelling whatever scrolled into it. The case that found
  // this: the cross-job rate table scrolls sideways under its own column
  // headers, where each header's tip IS that column's job name, so a stale
  // bubble names the wrong job. Closing is the honest answer and it is two
  // lines; re-placing on every scroll frame is neither. Capture phase,
  // because the panes and tables that scroll are elements, and a scroll event
  // on an element never bubbles to window.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => {
      setPinned(false);
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  // show()'s openUp is a guess made before the bubble exists (a fixed
  // room-above threshold), and content height varies — a titled five-line tip
  // near the top of the viewport can open upward and clip. After the bubble
  // mounts, measure the real box; if its top breaches the viewport edge, flip
  // it below the trigger. Layout effect so the correction lands before paint —
  // the user never sees the clipped frame.
  useLayoutEffect(() => {
    if (!open || !pos.openUp) return;
    const bubble = bubbleRef.current;
    const trigger = ref.current;
    if (!bubble || !trigger) return;
    if (bubble.getBoundingClientRect().top >= EDGE) return;
    const r = trigger.getBoundingClientRect();
    setPos((p) => ({ ...p, top: r.bottom + GAP, bottom: undefined, openUp: false }));
  }, [open, pos.openUp]);

  const cancelHide = () => window.clearTimeout(closeTimer.current);

  const show = () => {
    cancelHide();
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const center = r.left + r.width / 2;
    const left = Math.max(
      EDGE,
      Math.min(center - BUBBLE_WIDTH / 2, window.innerWidth - BUBBLE_WIDTH - EDGE),
    );
    // Open upward when there's room above the trigger; otherwise drop below.
    const openUp = r.top > 160;
    // Caret position within the bubble, in the bubble's own coordinate space
    // (distance from its left edge) — clamped so it can't land under the
    // rounded corners even when the bubble is pinned against a viewport edge.
    // 16px keeps the 7px-half-width caret clear of the 8px rounded corners.
    const caretX = Math.max(16, Math.min(center - left, BUBBLE_WIDTH - 16));
    setPos(
      openUp
        ? { left, bottom: window.innerHeight - r.top + GAP, caretX, openUp }
        : { left, top: r.bottom + GAP, caretX, openUp },
    );
    setOpen(true);
  };
  const hide = () => {
    if (pinned) return; // pin overrides hover/blur close entirely
    cancelHide();
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  const togglePin = () => {
    if (pinned) {
      setPinned(false);
      setOpen(false);
    } else {
      show();
      setPinned(true);
    }
  };

  return (
    <span
      ref={ref}
      className={`help-tip${label ? ' help-tip--labeled' : ''}`}
      role="button"
      // The labeled variant is a DISCLOSURE — it reveals content that is
      // otherwise absent — so it reports `aria-expanded`. The bare `?` keeps
      // `aria-pressed`: its bubble also appears on hover, and the click only
      // pins it, which is genuinely a toggle. Two affordances, two words.
      {...(label ? { 'aria-expanded': open } : { 'aria-pressed': pinned })}
      aria-describedby={open ? bubbleId : undefined}
      aria-label={label ? undefined : 'More information'}
      tabIndex={0}
      {...(inverse ? { style: { color: '#ffffff' } } : {})}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={togglePin}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          togglePin();
        }
      }}
    >
      {label ?? <HelpCircle size={15} aria-hidden />}
      {open &&
        createPortal(
          <span
            ref={bubbleRef}
            id={bubbleId}
            className={`help-tip-bubble help-tip-bubble--portal ${
              pos.openUp ? 'help-tip-bubble--up' : 'help-tip-bubble--down'
            }`}
            role="tooltip"
            onMouseEnter={cancelHide}
            onMouseLeave={hide}
            style={
              {
                position: 'fixed',
                left: pos.left,
                ...(pos.top !== undefined ? { top: pos.top } : {}),
                ...(pos.bottom !== undefined ? { bottom: pos.bottom } : {}),
                ['--caret-x' as string]: `${pos.caretX}px`,
              } as React.CSSProperties
            }
          >
            {title && <span className="help-tip-title">{title}</span>}
            {children}
            {learnMore && (
              <Link to={`/help/${learnMore}`} className="help-tip-more">
                Learn more
              </Link>
            )}
          </span>,
          document.body,
        )}
    </span>
  );
}
